import { Button, Screen, ScreenLead, ScreenTitle } from '@akpl/ui-mobile';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Link,
  Outlet,
  redirect,
  type RouterHistory,
  useNavigate,
} from '@tanstack/react-router';

import { OfflineBanner } from './ui/OfflineBanner';
import { Toasts } from './ui/Toasts';
import { useT } from './useT';
import { webRoutes } from './web/routes';

/** `?code=` is shared by `/tv` (TV user code) and `/guest` (session join code). */
function codeSearch(search: Record<string, unknown>): { code?: string } {
  const code = search.code;
  return typeof code === 'string' && code ? { code: code.toUpperCase() } : {};
}

function NotFound() {
  const t = useT();
  return (
    <Screen data-testid="not-found">
      {/* UX-412: "Algo salió mal" told a guest who mistyped a link that the product broke. */}
      <ScreenTitle>{t('mobile:notFoundTitle')}</ScreenTitle>
      <ScreenLead>{t('mobile:notFound')}</ScreenLead>
      <Link to="/guest">
        <Button>{t('mobile:sessionEnded.backToGuest')}</Button>
      </Link>
    </Screen>
  );
}

/**
 * Everything that must outlive a screen change lives here rather than in `AppShell` (UX-405).
 *
 * An expired session calls `showToast('La sesión terminó')` and then `navigate('/guest')` in the
 * same tick — and `<Toasts />` used to be mounted *inside* the `AppShell` that the navigation
 * unmounts, so in 95 DOM samples not one carried the message: the user simply found himself on
 * the join screen with no idea why. The offline banner (UX-404) is here for the same reason, and
 * because `/guest`, `/tv` and the auth screens have no shell of their own to host it.
 */
function RootLayout() {
  return (
    <>
      <OfflineBanner />
      <Outlet />
      <Toasts />
    </>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const tvRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tv',
  validateSearch: codeSearch,
  component: lazyRouteComponent(async () => {
    const { TvRoute } = await import('./routes/TvRoute');
    return {
      TvScreen: function TvScreen() {
        const { code } = tvRoute.useSearch();
        return <TvRoute code={code} />;
      },
    };
  }, 'TvScreen'),
});

const guestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest',
  validateSearch: codeSearch,
  component: lazyRouteComponent(async () => {
    const { GuestRoute } = await import('./routes/GuestRoute');
    return {
      GuestScreen: function GuestScreen() {
        const { code } = guestRoute.useSearch();
        const navigate = useNavigate();
        return (
          <GuestRoute code={code} onDone={() => void navigate({ to: '/app', replace: true })} />
        );
      },
    };
  }, 'GuestScreen'),
});

const authActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/action',
  validateSearch: (search: Record<string, unknown>) => ({
    mode: typeof search.mode === 'string' ? search.mode : undefined,
    oobCode: typeof search.oobCode === 'string' ? search.oobCode : undefined,
    continueUrl: typeof search.continueUrl === 'string' ? search.continueUrl : undefined,
  }),
  component: lazyRouteComponent(async () => {
    const { AuthActionRoute } = await import('./routes/AuthActionRoute');
    return {
      AuthActionScreen: function AuthActionScreen() {
        const search = authActionRoute.useSearch();
        return (
          <AuthActionRoute
            mode={search.mode}
            oobCode={search.oobCode}
            continueUrl={search.continueUrl}
          />
        );
      },
    };
  }, 'AuthActionScreen'),
});

/*
 * `/`, the public Home, is now what most visitors load first, so the flows of the TV stick (`/tv`,
 * `/guest`, `/app`, `/auth/action`) are chunks of their own: same behaviour, and the first paint
 * of each side stays within the budget of `tests/e2e/specs/budget.spec.ts`.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: lazyRouteComponent(() => import('./routes/AppShell'), 'AppShell'),
});

const appHomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./app/Home'), 'Home'),
});

const appSearchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'search',
  component: lazyRouteComponent(() => import('./app/Search'), 'Search'),
});

/**
 * Fila and the playlist/artist list are the only screens behind a dynamic import (QA
 * 2026-09-14, MOB-56): Fila carries dnd-kit (~130 KB of source, the single biggest dependency
 * of the `/app` screens) and neither is on the first screen a phone paints, so both stay out of
 * the entry chunk and are fetched on the tab that needs them.
 */
const appQueueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'queue',
  component: lazyRouteComponent(() => import('./app/Queue'), 'Queue'),
});

const appProfileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'profile',
  component: lazyRouteComponent(() => import('./app/Profile'), 'Profile'),
});

/** A playlist or an artist opened from Home or Search (docs/05 §4, Figma M-C3). */
const appPlaylistRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'playlist/$id',
  component: lazyRouteComponent(async () => {
    const { PlaylistView } = await import('./app/PlaylistView');
    return {
      PlaylistScreen: function PlaylistScreen() {
        const { id } = appPlaylistRoute.useParams();
        return <PlaylistView id={id} kind="playlist" />;
      },
    };
  }, 'PlaylistScreen'),
});

const appArtistRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'artist/$id',
  component: lazyRouteComponent(async () => {
    const { PlaylistView } = await import('./app/PlaylistView');
    return {
      ArtistScreen: function ArtistScreen() {
        const { id } = appArtistRoute.useParams();
        return <PlaylistView id={id} kind="artist" />;
      },
    };
  }, 'ArtistScreen'),
});

/**
 * `/app/subscribe` exists but redirects to Profile while `settings.billing.checkout_enabled`
 * is false (docs/05 §4, phase-1 decision 2026-09-08): a link out in the wild must not 404.
 */
const appSubscribeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'subscribe',
  beforeLoad: () => {
    throw redirect({ to: '/app/profile', replace: true });
  },
});

const routeTree = rootRoute.addChildren([
  webRoutes(rootRoute),
  tvRoute,
  guestRoute,
  authActionRoute,
  appRoute.addChildren([
    appHomeRoute,
    appSearchRoute,
    appQueueRoute,
    appProfileRoute,
    appPlaylistRoute,
    appArtistRoute,
    appSubscribeRoute,
  ]),
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    defaultNotFoundComponent: NotFound,
    ...(history ? { history } : {}),
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
